import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import { SurfaceCard } from '../../design/SurfaceCard'
import { ChipTabs } from '../../design/ChipTabs'
import { formatDuration } from './progressMath'
import type { MyProgress } from './useMyProgress'

type Metric = 'tops' | 'points' | 'minutes'

const DAY_MS = 86_400_000

interface Row {
  label: string
  /** The days this bar covers, e.g. "17 – 19 Aug", shown in the tooltip. */
  range: string
  current: number
  previous: number
}

function ChartTooltip({
  active,
  payload,
  metric,
}: {
  active?: boolean
  payload?: { dataKey: string; value: number; payload: Row }[]
  metric: Metric
}) {
  const { t } = useTranslation()
  if (!active || !payload?.length) return null
  const fmt = (v: number) => (metric === 'minutes' ? formatDuration(v) : String(v))
  const value = (key: string) => payload.find((p) => p.dataKey === key)?.value ?? 0
  return (
    <div className="rounded-sm border border-[var(--gym-line)] bg-content1 px-3 py-2 shadow-e2">
      <p className="text-xs font-semibold">{payload[0].payload.range}</p>
      <p className="text-xs">
        {t('progress.legendNow')}: {fmt(value('current'))}
      </p>
      <p className="text-xs text-foreground/50">
        {t('progress.legendBefore')}: {fmt(value('previous'))}
      </p>
    </div>
  )
}

/**
 * The window bar by bar, each bar beside the same stretch of the window before.
 *
 * The previous period used to be a dashed line. Over a member who logged
 * nothing last month it was a flat line at zero running under everything, which
 * read as a broken chart rather than as "you did none of this before". A paired
 * bar says the same thing and matches the grades chart.
 */
export function ActivityChart({ data, locale }: { data: MyProgress; locale: string }) {
  const { t } = useTranslation()
  const [metric, setMetric] = useState<Metric>('tops')

  const start = new Date(data.currentStart).getTime()
  const day = (ms: number) =>
    new Date(ms).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' })

  const rows: Row[] = data.series.map((point, i) => {
    const from = start + i * data.bucketDays * DAY_MS
    const to = from + (data.bucketDays - 1) * DAY_MS
    return {
      label: day(from),
      range: data.bucketDays === 1 ? day(from) : `${day(from)} – ${day(to)}`,
      current: point.current[metric],
      previous: point.previous[metric],
    }
  })

  const summary = rows.map((r) => `${r.range}: ${r.current}`).join(', ')

  return (
    <SurfaceCard className="p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">{t('progress.activityTitle')}</p>
          {/* Say how wide a bar is: 10 bars over 30 days is not self-evident. */}
          <p className="text-xs text-foreground/50">
            {t('progress.activityBucket', { count: data.bucketDays })}
          </p>
        </div>
        <ChipTabs
          aria-label={t('progress.activityTitle')}
          value={metric}
          onChange={setMetric}
          options={[
            { key: 'tops', label: t('progress.metricTops') },
            { key: 'points', label: t('progress.metricPoints') },
            { key: 'minutes', label: t('progress.metricTime') },
          ]}
        />
      </div>
      <div role="img" aria-label={`${t('progress.activityTitle')}. ${summary}`}>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={rows} margin={{ top: 8, right: 4, left: 4, bottom: 0 }} barGap={2}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: 'hsl(var(--heroui-foreground) / 0.45)' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={12}
            />
            <Tooltip
              content={<ChartTooltip metric={metric} />}
              cursor={{ fill: 'var(--gym-ink)', fillOpacity: 0.07 }}
            />
            <Bar
              dataKey="previous"
              fill="hsl(var(--heroui-foreground) / 0.18)"
              radius={[4, 4, 0, 0]}
              maxBarSize={18}
            />
            <Bar dataKey="current" fill="var(--gym-ink)" radius={[4, 4, 0, 0]} maxBarSize={18} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex gap-4 text-xs text-foreground/50">
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="h-2.5 w-2.5 rounded-sm bg-[var(--gym-ink)]" />
          {t('progress.legendNow')}
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="h-2.5 w-2.5 rounded-sm bg-foreground/20" />
          {t('progress.legendBefore')}
        </span>
      </div>
    </SurfaceCard>
  )
}
